import { useCallback, useState } from 'react';

import {
  MailUserMailboxesListQuery,
  useGrantMailboxAccessMutation,
  useMailUserMailboxesListQuery,
} from 'core/graphql';

import { IMailResult, mailFailure } from './mailModel';

export type IMailUserMailbox = MailUserMailboxesListQuery['mailUserMailboxes'][number];

const EMPTY: IMailUserMailbox[] = [];

/**
 * ТЗ 0122 — «Почтовые ящики» сотрудника в «Пользователях» (только Admin):
 * какие ящики он видит и может ли отправлять. Свой личный ящик —
 * неявный доступ (`implicit`), его не снять.
 */
export const useMailboxAccess = (userId: string | null, skip = false) => {
  const { data, loading, error, refetch } = useMailUserMailboxesListQuery({
    variables: { userId: userId || '' },
    skip: skip || !userId,
    fetchPolicy: 'network-only',
  });
  const [grantMut] = useGrantMailboxAccessMutation();
  const [busyId, setBusyId] = useState<string | null>(null);

  const setAccess = useCallback(
    async (
      mailboxId: string,
      enabled: boolean,
      canSend: boolean,
    ): Promise<IMailResult> => {
      if (!userId) return { ok: false, errorCode: 'NOT_FOUND' };
      setBusyId(mailboxId);
      try {
        const { data: result } = await grantMut({
          variables: { mailboxId, userId, enabled, canSend },
        });
        const payload = result?.setMailboxAccess;
        await refetch().catch(() => undefined);
        return payload
          ? {
              ok: payload.ok,
              error: payload.error,
              errorCode: payload.errorCode,
            }
          : { ok: false, errorCode: 'UNAVAILABLE' };
      } catch (caught) {
        return mailFailure(caught);
      } finally {
        setBusyId(null);
      }
    },
    [userId, grantMut, refetch],
  );

  return {
    mailboxes: data?.mailUserMailboxes ?? EMPTY,
    loading: loading && !data,
    failed: Boolean(error) && !data,
    busyId,
    setAccess,
  };
};
